import { useState, type ReactElement, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';

import type { ClusterGrantCapability, TenantViewModel } from '../api';
import type { CreateClusterGrantRequestDto } from '../endpoints';

interface FieldErrors {
  user?: string;
  tenant?: string;
}

const EMPTY = { globalUserGuid: '', tenantUuid: '', capability: 'VIEW' as ClusterGrantCapability };

export function CreateGrantDialog({
  open,
  onOpenChange,
  tenants,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly tenants: readonly TenantViewModel[];
  readonly onSubmit: (input: CreateClusterGrantRequestDto) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(EMPTY);
  const [errors, setErrors] = useState<FieldErrors>({});

  function handleClose(next: boolean): void {
    if (!next) {
      setDraft(EMPTY);
      setErrors({});
    }
    onOpenChange(next);
  }

  function handleSubmit(event: SyntheticEvent): void {
    event.preventDefault();
    const next: FieldErrors = {};
    const guid = draft.globalUserGuid.trim();
    if (guid.length === 0) next.user = t('routes.platform.grants.create.errors.userRequired');
    if (draft.tenantUuid.length === 0) {
      next.tenant = t('routes.platform.grants.create.errors.tenantRequired');
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({
      globalUserGuid: guid,
      tenantUuid: draft.tenantUuid,
      capability: draft.capability,
    });
  }

  const submitErrorCode = getErrorCodeForUi(submitError);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.platform.grants.create.title')}</DialogTitle>
          <DialogDescription>{t('routes.platform.grants.create.description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <FormField
            id="create-grant-user"
            label={t('routes.platform.grants.create.fields.user')}
            required
            error={errors.user}
          >
            {(fp) => (
              <Input
                {...fp}
                value={draft.globalUserGuid}
                onChange={(e) => { setDraft((p) => ({ ...p, globalUserGuid: e.target.value })); }}
                autoComplete="off"
                className={`${fp.className} font-mono`}
              />
            )}
          </FormField>
          <FormField
            id="create-grant-tenant"
            label={t('routes.platform.grants.create.fields.tenant')}
            required
            error={errors.tenant}
          >
            {(fp) => (
              <Select
                {...fp}
                value={draft.tenantUuid}
                onChange={(e) => { setDraft((p) => ({ ...p, tenantUuid: e.target.value })); }}
              >
                <option value="">{t('routes.platform.grants.create.selectTenant')}</option>
                {tenants.map((tenant) => (
                  <option key={tenant.uuid} value={tenant.uuid}>
                    {tenant.label.length > 0 ? `${tenant.label} (${tenant.name})` : tenant.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField
            id="create-grant-capability"
            label={t('routes.platform.grants.create.fields.capability')}
          >
            {(fp) => (
              <Select
                {...fp}
                value={draft.capability}
                onChange={(e) =>
                  { setDraft((p) => ({ ...p, capability: e.target.value as ClusterGrantCapability })); }
                }
              >
                <option value="ADMINISTER">ADMINISTER</option>
                <option value="OPERATE">OPERATE</option>
                <option value="VIEW">VIEW</option>
              </Select>
            )}
          </FormField>
          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.platform.grants.create.errors.submit', { code: submitErrorCode })}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { handleClose(false); }}
              disabled={pending}
            >
              {t('routes.platform.grants.create.cancel')}
            </Button>
            <Button type="submit" loading={pending}>
              {t('routes.platform.grants.create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
